/**
 * Fraction Kingdom, surveyed: the village map and the inside of all five
 * regions (`docs/rpg.md` §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **The geography is the one crossing.** The kingdom is a river valley, the
 * high road runs the length of it, and there is exactly one bridge — with the
 * mill standing on it. Slice Village is the near bank, where a fraction is
 * still one thing cut up; the kitchens, the hollow and the market are all on
 * the far bank, and everything on that side is reached across the mill. That is
 * the mathematics and not a decoration over it: adding unlike parts, comparing
 * them, dividing by them and quoting one against another all wait on being able
 * to grind two fractions to the same grade, which is what the Equivalence Mill
 * is for. A student who walks from the village to the market has crossed at the
 * mill, because there is nowhere else to cross.
 *
 * Terrain is scenery here as everywhere else. The river and the hollow's rim
 * refuse a *step* and never a region: `validateTown` and `validateRegionMap`
 * walk all six of these from their arrival tiles on every `npm run
 * content:validate`, and every region and every concept below is an ordinary
 * card and an ordinary link on the kingdom page besides.
 *
 * ```
 *   .  meadow     #  lane / path   ~  water      _  floor
 *   f  trees      =  bridge        ^  hillside   %  wall
 *   ,  green      "  crop rows
 * ```
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-fraction';

/* -------------------------------------------------------------------------- */
/* The village                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The valley: the river, the crossing, and the mill standing on it.
 *
 * West of the water is Slice Village, its green cut into two plots of exactly
 * the same size by the lane running up the middle — the whole one, and what it
 * was cut into. East of the water everything is downstream work: the kitchens
 * with their gardens on the high ground north, the market green with its stalls
 * east, and Divider's Hollow dropped below the road in a rim of hillside,
 * because that is the one place in this kingdom where dividing makes the answer
 * larger and the ground ought to fall away.
 *
 * The mill has the crossing and the road passes its door rather than its floor,
 * so nobody is walked into it on their way east. The gate at the far end of the
 * high road is the boss, and the road home runs west out of the valley.
 */
const FRACTION_VALLEY: Town = {
  kingdomId: KINGDOM,
  caption:
    'One river, one crossing, and the mill standing on it: the village on the near bank, and the kitchens, the hollow and the market on the far one.',
  width: 26,
  height: 17,
  /* On the high road three paces west of the bridge, with the village lane on
     one side and the crossing on the other. Where a student is standing is
     never stored, so every visit to the valley starts here. */
  start: { x: 7, y: 10 },
  rows: [
    'fffffffffff~ffffffffffffff',
    'f.,,,,,....~.....%%%%%%..f',
    'f.,,,,,..f.~.""".%____%..f',
    'f...#...f..~.""".%____%..f',
    'f"""#"""...~.""".%____%..f',
    'f"""#"""...~.....%%_%%%..f',
    'f"""#""".f.~%%%%%........f',
    'f...#......~%___%...,%,,.f',
    'f.f.#......~%___%...,,,,.f',
    'f...#...#..~%%_%%...,,,%^f',
    'f##########=#############f',
    'f..........~....#.......^f',
    'f........f.~.^^^#^^^^..^^f',
    'f..f.......~.^......^.f^^f',
    'f.f....f...~.^......^....f',
    'f..........~.^^^^^^^^.f..f',
    'fffffffffff~ffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Road Home', emoji: '🗺️', x: 1, y: 10 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 8, y: 9 },
    { kind: 'region', regionId: 'region-slice-village', x: 4, y: 3 },
    { kind: 'region', regionId: 'region-equivalence-mill', x: 14, y: 7 },
    { kind: 'region', regionId: 'region-mixing-kitchens', x: 19, y: 3 },
    { kind: 'region', regionId: 'region-dividers-hollow', x: 16, y: 13 },
    { kind: 'region', regionId: 'region-ratio-market', x: 22, y: 8 },
    { kind: 'boss', bossId: 'boss-fraction-kingdom', x: 24, y: 10 },
  ],
};

/** The kingdom's village map. */
export const FRACTION_TOWN: Town = FRACTION_VALLEY;

/* -------------------------------------------------------------------------- */
/* Slice Village                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Four plots the same size, a street marked off in equal paces, and the
 * bakehouse.
 *
 * The village's own trick is remembering what the whole one was, so the picture
 * is four plots of identical size with different things growing on them: the
 * plot is the whole, and it did not change when it was cut. Fraction basics is
 * inside the bakehouse where the cutting is actually done; the ruled walk along
 * the top is stepped off in equal paces with a mark at every other one, and the
 * zero post at its west end is where fractions on the number line stands. Whole
 * loaves are stacked in the granary yard, which is the answer to what four
 * quarters comes to.
 */
const SLICE_VILLAGE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-slice-village',
  caption:
    'Four plots of exactly the same size, a walk stepped off in equal paces, and the bakehouse where the cutting is done.',
  width: 18,
  height: 13,
  /* On the street, four paces in from the gate, with the bakehouse door and the
     granary lane both in sight. */
  start: { x: 12, y: 6 },
  rows: [
    'ffffffffffffffffff',
    'f.,.,.,.,.,.,.,.,f',
    'f################f',
    'ff......#.......ff',
    'f"""."""#""".""".f',
    'f"""."""#""".""".f',
    'f################f',
    'f.f.............ff',
    'f.%%%_%%%."""#"""f',
    'f.%_____%."""#"""f',
    'f.%_____%."""#"""f',
    'f.%%%%%%%f"""""""f',
    'ffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'fraction-basics', x: 5, y: 10 },
    { kind: 'concept', conceptId: 'fractions-on-number-line', x: 1, y: 2 },
    { kind: 'concept', conceptId: 'whole-number-fractions', x: 13, y: 10 },
    { kind: 'road', name: 'The River Road', emoji: '🌉', x: 16, y: 6 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Equivalence Mill                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The mill on the crossing: two hoppers, one pair of stones, and the scales.
 *
 * The grinding floor is the argument. Two hoppers stand on one side of the
 * spine and a single pair of stones on the other, which is what a common
 * denominator is before anybody writes one down — two different things brought
 * to one grade. The stones themselves grind a fraction into another name for
 * itself; the sieve rack below sorts down to the smallest it will go; and the
 * two pans of the scales at the back are where any two of them can finally be
 * held against each other. The race runs along the west wall, because the mill
 * is the thing standing on the kingdom's one crossing.
 */
const EQUIVALENCE_MILL: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-equivalence-mill',
  caption:
    'The race along the wall, two hoppers feeding one pair of stones, the sieve rack below and the scales at the back.',
  width: 18,
  height: 13,
  /* On the grinding floor a step in from the door, facing up the mill. */
  start: { x: 9, y: 10 },
  rows: [
    'ffffffffffffffffff',
    'f~~%%%%%%%%%%%%%%f',
    'f~~%_%_%_%_%%%__%f',
    'f~~%_%_%_%_%%%__%f',
    'f~~%_____%______%f',
    'f~~%_____%______%f',
    'f~~%____________%f',
    'f~~%____________%f',
    'f~~%_%%%____%_%_%f',
    'f~~%____________%f',
    'f~~%____________%f',
    'f~~%%%%%%_%%%%%%%f',
    'ffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'common-denominators', x: 6, y: 4 },
    { kind: 'concept', conceptId: 'equivalent-fractions', x: 12, y: 4 },
    { kind: 'concept', conceptId: 'simplify-fractions', x: 6, y: 9 },
    { kind: 'concept', conceptId: 'compare-fractions', x: 13, y: 9 },
    { kind: 'road', name: 'The Mill Door', emoji: '🌉', x: 9, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Mixing Kitchens                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Two kitchens under one roof, with a wall between them and one door through.
 *
 * The wall is the point: adding parts and multiplying them follow stubbornly
 * different rules, and drawing the kitchens as one open room would have said
 * the opposite. West is the adding side — the range along the top with the two
 * pots on it, and the larder below the partition where whole loaves are kept
 * beside the parts of loaves. East is the multiplying side, laid out as a
 * lattice of posts, because a rectangle cut both ways is what multiplying two
 * fractions *is*. The one doorway between them is at the middle of the wall,
 * and it is a door rather than an opening.
 */
const MIXING_KITCHENS: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-mixing-kitchens',
  caption:
    'One roof, two kitchens and a wall between them: the range and the larder on the adding side, a lattice cut both ways on the other.',
  width: 20,
  height: 13,
  /* In the adding kitchen, in from the door, with the larder shelves beside
     you and the doorway through at the far corner. */
  start: { x: 5, y: 9 },
  rows: [
    'ffffffffffffffffffff',
    'f.%%%%%%%%%%%%%%%%.f',
    'f.%_%%%%__%______%.f',
    'f.%_______%_%_%__%.f',
    'f.%_______%______%.f',
    'f.%_______%_%_%__%.f',
    'f.%%%%%%%________%.f',
    'f.%%_%____%_%_%__%.f',
    'f.%%______%______%.f',
    'f.%_______%_%_%__%.f',
    'f.%%%_%%%%%%%%%%%%.f',
    'f..................f',
    'ffffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'add-like-fractions', x: 4, y: 3 },
    { kind: 'concept', conceptId: 'add-fractions', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'mixed-numbers', x: 4, y: 8 },
    { kind: 'concept', conceptId: 'add-mixed-numbers', x: 7, y: 8 },
    { kind: 'concept', conceptId: 'fraction-times-whole', x: 11, y: 2 },
    { kind: 'concept', conceptId: 'multiply-fractions', x: 13, y: 8 },
    { kind: 'road', name: 'The Kitchen Door', emoji: '🌉', x: 5, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Divider's Hollow                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One channel comes down off the hillside, and four go out of the sluice.
 *
 * This is the valley where dividing makes things bigger, so the water does it
 * where a student can see: a single channel off the high ground, a sluice gate,
 * and four channels running out below it. Fraction as division stands where the
 * one channel arrives; the two divide concepts are at the heads of two of the
 * four, so getting to either means walking up a channel that did not exist
 * before the sluice. The terraces on the west slope are the same field stepped
 * out one row wider each time, which is what scaling by a fraction does to it,
 * and the path back to the high road climbs out of the rim at the top.
 */
const DIVIDERS_HOLLOW: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-dividers-hollow',
  caption:
    'One channel down off the hillside, a sluice gate, and four channels out below it — with the terraces stepping wider down the west slope.',
  width: 19,
  height: 14,
  /* On the floor of the hollow, a step down from the path, with the sluice
     ahead of you. */
  start: { x: 9, y: 4 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^#^^~^^^^^^',
    '^^^^.....#..~..^^^^',
    '^^.......#..~....^^',
    '^...........~.....^',
    '^.........%%~%%...^',
    '^.......~~~~~~~~~.^',
    '^."......~.~.~.~..^',
    '^."".....~.~.~.~..^',
    '^."""....~.~.~.~..^',
    '^.""""...~.~.~.~..^',
    '^........~.~.~.~..^',
    '^.................^',
    '^^^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'fraction-as-division', x: 11, y: 3 },
    { kind: 'concept', conceptId: 'divide-unit-fractions', x: 10, y: 7 },
    { kind: 'concept', conceptId: 'divide-fractions', x: 14, y: 7 },
    { kind: 'concept', conceptId: 'fraction-scaling', x: 3, y: 11 },
    { kind: 'road', name: 'The Hollow Path', emoji: '🌉', x: 9, y: 1 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Ratio Market                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Ten paces by ten of open green, with the ranges of stalls around it.
 *
 * Nothing in this market is priced on its own, so nothing on the map stands on
 * its own either. The stalls come in a range down each side and they are
 * plainly not the same size — one of the western three is twice either of its
 * neighbours — and the traders stand in the gaps between them, which is where
 * ratios and unit rates are. The square itself is ten paces by ten, and the
 * market cross at the middle of the hundred is where percentages stands.
 * Proportions and the counting house are the two gaps in the eastern range, and
 * the gate in the south wall is the road back to the valley.
 */
const RATIO_MARKET: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-ratio-market',
  caption: 'Ten paces by ten of open green, ranges of unequal stalls down both sides, and the market cross at the middle.',
  width: 20,
  height: 14,
  /* On the green, inside the gate and off the line of it, so the cross at the
     centre is something to walk to rather than something to walk into. */
  start: { x: 11, y: 10 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%%',
    '%..##############..%',
    '%%%..,,,,,,,,,,..%%%',
    '%%%..,,,,,,,,,,..%%%',
    '%....,,,,,,,,,,..%%%',
    '%%%..,,,,,,,,,,....%',
    '%%%..,,,,,,,,,,..%%%',
    '%%%..,,,,,,,,,,..%%%',
    '%%%..,,,,,,,,,,..%%%',
    '%....,,,,,,,,,,....%',
    '%%%..,,,,,,,,,,..%%%',
    '%%%..,,,,,,,,,,..%%%',
    '%..##############..%',
    '%%%%%%%%%#%%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'ratios', x: 1, y: 4 },
    { kind: 'concept', conceptId: 'unit-rates', x: 1, y: 9 },
    { kind: 'concept', conceptId: 'percentages', x: 9, y: 6 },
    { kind: 'concept', conceptId: 'proportions', x: 18, y: 5 },
    { kind: 'concept', conceptId: 'percent-applications', x: 18, y: 9 },
    { kind: 'road', name: 'The Market Gate', emoji: '🌉', x: 9, y: 13 },
  ],
};

/** Every region of Fraction Kingdom, surveyed. */
export const FRACTION_REGION_MAPS: readonly RegionMap[] = [
  SLICE_VILLAGE,
  EQUIVALENCE_MILL,
  MIXING_KITCHENS,
  DIVIDERS_HOLLOW,
  RATIO_MARKET,
];
